import {
    EnterpriseHandle,
    OrganizationHandle,
    OrganizationPublicHandle,
    UserHandle,
} from "@buildwithflux/identifiers-schema/handles";

import {
    accountIsEnterpriseForUrlRouting,
    accountIsOrganizationForUrlRouting,
    accountIsUserForUrlRouting,
    AccountPublicHandleForUrlRouting,
    AccountWithDetailsForUrlRouting,
    EnterpriseForUrlRouting,
    EnterpriseOrganizationForUrlRouting,
    isEnterpriseOrganizationForUrlRouting,
    isNonEnterpriseOrganizationForUrlRouting,
    NonEnterpriseOrganizationForUrlRouting,
    OrganizationForUrlRouting,
    UserForUrlRouting,
    WithAccountHandlesForUrlRouting,
} from "./accounts";
import {ReverseUrlRoutingError} from "./error";
import {DocumentDataForOwnerProfileUrlRouting} from "./models";
import {toAbsoluteUrl} from "./origin";
import {joinPathParts} from "./path";

/*
 * =========== Forward routing: ===========
 */

const ProfileBaseRoutes = ["/(enterprise)?/:handle", "/enterprise/:handle/-/:enterpriseOrganizationHandle"] as const;

export const ProfileSubRoutes = [
    "api-keys",
    "billing",
    "billing/invoices",
    "billing/information",
    "members",
    "settings",
    "starred",
    "upgrade",
    "changeplan",
    "plan",
    "knowledge",
    "shared",
] as const;
export type ProfileSubRoute = (typeof ProfileSubRoutes)[number];

/**
 * A list of all possible profile routes
 *
 * The `handle` parameter is an enterprise handle  if the route params have an optional `0` parameter with value `enterprise`,
 * or if they have a parameter named `enterpriseOrganizationHandle`.
 */
export const ProfileRoutes: string[] = [
    ...ProfileBaseRoutes,
    ...ProfileBaseRoutes.flatMap((baseRoute) => ProfileSubRoutes.map((subRoute) => joinPathParts(baseRoute, subRoute))),
];

/*
 * =========== Reverse routing: ===========
 */

type GetProfileUrlParams = {
    paymentSuccess?: boolean;
};

export type GetProfileUrlOptions = {
    subRoute?: ProfileSubRoute;
    urlParams?: GetProfileUrlParams;
};

/**
 * Most up-to-date and correct reverse routing function for finding the profile URL of an account or enterprise
 */
export function getProfileUrl(enterprise: EnterpriseForUrlRouting, options?: GetProfileUrlOptions): string;
export function getProfileUrl(organization: OrganizationForUrlRouting, options?: GetProfileUrlOptions): string;
export function getProfileUrl(
    organization: OrganizationForUrlRouting,
    enterprise?: EnterpriseForUrlRouting | undefined,
    options?: GetProfileUrlOptions,
): string;
export function getProfileUrl(user: UserForUrlRouting, options?: GetProfileUrlOptions): string;
export function getProfileUrl(
    account: EnterpriseForUrlRouting | OrganizationForUrlRouting | UserForUrlRouting,
    enterpriseOfOrganizationOrOptions?: EnterpriseForUrlRouting | GetProfileUrlOptions | undefined,
    options: GetProfileUrlOptions = {},
): string {
    // Argument handling
    const enterprise: EnterpriseForUrlRouting | undefined = accountIsEnterpriseForUrlRouting(account)
        ? account
        : enterpriseOfOrganizationOrOptions && "uid" in enterpriseOfOrganizationOrOptions
          ? enterpriseOfOrganizationOrOptions
          : undefined;
    const organization: OrganizationForUrlRouting | undefined = accountIsOrganizationForUrlRouting(account)
        ? account
        : undefined;
    const user: UserForUrlRouting | undefined = accountIsUserForUrlRouting(account) ? account : undefined;
    const optionsArg: GetProfileUrlOptions | undefined =
        enterpriseOfOrganizationOrOptions && !("uid" in enterpriseOfOrganizationOrOptions)
            ? enterpriseOfOrganizationOrOptions
            : options;

    if (organization) {
        if (isEnterpriseOrganizationForUrlRouting(organization)) {
            if (!enterprise) {
                throw new ReverseUrlRoutingError(
                    "Invalid arguments to getProfileUrl: enterprise organization without an enterprise passed",
                );
            }
            if (enterprise.uid !== organization.enterpriseUid) {
                throw new ReverseUrlRoutingError(
                    "Invalid arguments to getProfileUrl: enterprise organization does not match enterprise passed",
                );
            }
            return getOrganizationProfileUrlForEnterpriseOrganization(organization, enterprise, optionsArg);
        } else if (isNonEnterpriseOrganizationForUrlRouting(organization)) {
            if (enterprise) {
                throw new ReverseUrlRoutingError(
                    "Invalid arguments to getProfileUrl: non-enterprise organization with an enterprise passed",
                );
            }
            return getOrganizationProfileUrlForNonEnterpriseOrganization(organization, optionsArg);
        } else {
            throw new ReverseUrlRoutingError(
                "Invalid organization type: not enterprise nor non-enterprise organization found",
            );
        }
    } else if (user) {
        if (enterprise) {
            throw new ReverseUrlRoutingError(
                "Invalid arguments to getProfileUrl: user does not have an enterprise directly",
            );
        }
        return getUserProfileUrl(user, optionsArg);
    } else if (enterprise) {
        return getEnterpriseProfileUrl(enterprise, optionsArg);
    } else {
        throw new ReverseUrlRoutingError(
            "Invalid arguments to getProfileUrl: no user, organization nor enterprise found",
        );
    }
}

/**
 * Most up-to-date and correct reverse routing function for finding the absolute profile URL of an account or enterprise
 */
export function getAbsoluteProfileUrl(enterprise: EnterpriseForUrlRouting, options?: GetProfileUrlOptions): string;
export function getAbsoluteProfileUrl(
    organization: OrganizationForUrlRouting,
    enterprise?: EnterpriseForUrlRouting,
    options?: GetProfileUrlOptions,
): string;
export function getAbsoluteProfileUrl(user: UserForUrlRouting, options?: GetProfileUrlOptions): string;
export function getAbsoluteProfileUrl(
    account: EnterpriseForUrlRouting | OrganizationForUrlRouting | UserForUrlRouting,
    enterpriseOfOrganizationOrOptions?: EnterpriseForUrlRouting | GetProfileUrlOptions,
    options: GetProfileUrlOptions = {},
): string {
    // Argument handling
    const enterprise: EnterpriseForUrlRouting | undefined = accountIsEnterpriseForUrlRouting(account)
        ? account
        : enterpriseOfOrganizationOrOptions && "uid" in enterpriseOfOrganizationOrOptions
          ? enterpriseOfOrganizationOrOptions
          : undefined;
    const organization: OrganizationForUrlRouting | undefined = accountIsOrganizationForUrlRouting(account)
        ? account
        : undefined;
    const user: UserForUrlRouting | undefined = accountIsUserForUrlRouting(account) ? account : undefined;
    const optionsArg: GetProfileUrlOptions | undefined =
        !enterpriseOfOrganizationOrOptions || !("uid" in enterpriseOfOrganizationOrOptions)
            ? enterpriseOfOrganizationOrOptions
            : options;

    if (organization) {
        if (enterprise) {
            return toAbsoluteUrl(getProfileUrl(organization, enterprise, optionsArg));
        } else {
            return toAbsoluteUrl(getProfileUrl(organization, optionsArg));
        }
    } else if (user) {
        return toAbsoluteUrl(getProfileUrl(user, optionsArg));
    } else if (enterprise) {
        return toAbsoluteUrl(getProfileUrl(enterprise, optionsArg));
    } else {
        throw new ReverseUrlRoutingError("Invalid account type passed to getAbsoluteProfileUrl");
    }
}

// ---- Constituent functions of the above two functions

export function getOrganizationProfileUrlForEnterpriseOrganization(
    organization: EnterpriseOrganizationForUrlRouting,
    enterprise: EnterpriseForUrlRouting,
    options: GetProfileUrlOptions = {},
): string {
    if (organization.uid === enterprise.defaultOrganizationUid) {
        return getEnterpriseProfileUrl(enterprise, options);
    }

    return (
        joinPathParts(
            "/",
            "enterprise",
            enterprise.handle.toLowerCase(),
            "-",
            organization.handle.toLowerCase(),
            options.subRoute,
        ) + getUrlParamsForProfileUrl(options)
    );
}

export function getOrganizationProfileUrlForNonEnterpriseOrganization(
    organization: NonEnterpriseOrganizationForUrlRouting,
    options: GetProfileUrlOptions = {},
): string {
    return joinPathParts("/", organization.handle.toLowerCase(), options.subRoute) + getUrlParamsForProfileUrl(options);
}

export function getEnterpriseProfileUrl(enterprise: EnterpriseForUrlRouting, options: GetProfileUrlOptions = {}) {
    return (
        joinPathParts("/enterprise", enterprise.handle.toLowerCase(), options.subRoute) +
        getUrlParamsForProfileUrl(options)
    );
}

export function getUserProfileUrl(user: UserForUrlRouting, options: GetProfileUrlOptions = {}): string {
    return joinPathParts(`/`, user.handle.toLowerCase(), options.subRoute) + getUrlParamsForProfileUrl(options);
}

function getUrlParamsForProfileUrl(options: GetProfileUrlOptions): string {
    if (!options.urlParams || !options.urlParams.paymentSuccess) {
        // When more params are added, check for none being set
        return "";
    }

    const searchParams = new URLSearchParams();
    if (options.urlParams.paymentSuccess) {
        searchParams.set("paymentSuccess", "true");
    }
    return `?${searchParams.toString()}`;
}

// ----- Other useful functions for special situations

/*
 * In some cases, such as when trying to route to a user profile from a user handle, or from an IDocumentData object,
 * we need to directly use the handles -- these low-level functions should not be exported, and we should instead export
 * functions for higher-level specific use-cases, such as getProfileUrlForDocumentOwner()
 */

export function getUserOrNonEnterpriseOrganizationProfileUrlFromHandle(
    handle: UserHandle | OrganizationPublicHandle,
    options: GetProfileUrlOptions = {},
): string {
    return joinPathParts("/", handle.toLowerCase(), options.subRoute) + getUrlParamsForProfileUrl(options);
}

export function getEnterpriseProfileUrlFromHandle(
    enterpriseHandle: EnterpriseHandle,
    options: GetProfileUrlOptions = {},
): string {
    return (
        joinPathParts("/enterprise", enterpriseHandle.toLowerCase(), options.subRoute) +
        getUrlParamsForProfileUrl(options)
    );
}

export function getEnterpriseOrganizationProfileUrlFromHandle(
    enterpriseHandle: EnterpriseHandle,
    organizationHandle: OrganizationHandle,
    options: GetProfileUrlOptions = {},
): string {
    return (
        joinPathParts(
            "/enterprise",
            enterpriseHandle.toLowerCase(),
            "-",
            organizationHandle.toLowerCase(),
            options.subRoute,
        ) + getUrlParamsForProfileUrl(options)
    );
}

export function getProfileUrlForAccountHandles<T extends object>(props: WithAccountHandlesForUrlRouting<T>): string {
    if (props.enterpriseHandle) {
        if (props.enterpriseOrganizationHandle) {
            return getEnterpriseOrganizationProfileUrlFromHandle(
                props.enterpriseHandle,
                props.enterpriseOrganizationHandle,
            );
        }

        return getEnterpriseProfileUrlFromHandle(props.enterpriseHandle);
    }

    if (props.accountHandle) {
        return getUserOrNonEnterpriseOrganizationProfileUrlFromHandle(props.accountHandle);
    }

    throw new Error("Expected either enterpriseHandle or accountHandle to be provided");
}

export function getProfileUrlForDocumentOwner(
    document: DocumentDataForOwnerProfileUrlRouting,
    options: GetProfileUrlOptions = {},
): string {
    if (document.enterprise_owner_uid) {
        if (!document.enterprise_owner_handle) {
            throw new ReverseUrlRoutingError("Enterprise-owned project must have a enterprise_owner_handle set");
        }

        if (document.organization_owner_is_enterprise_default) {
            return getEnterpriseProfileUrlFromHandle(document.enterprise_owner_handle, options);
        } else {
            return getEnterpriseOrganizationProfileUrlFromHandle(
                document.enterprise_owner_handle,
                OrganizationHandle.parse(document.owner_handle),
                options,
            );
        }
    }

    return getUserOrNonEnterpriseOrganizationProfileUrlFromHandle(document.owner_handle, options);
}

export function getAbsoluteProfileUrlForDocumentOwner(
    document: DocumentDataForOwnerProfileUrlRouting,
    options: GetProfileUrlOptions = {},
): string {
    return toAbsoluteUrl(getProfileUrlForDocumentOwner(document, options));
}

export function getProfileUrlForAccountWithDetails(
    account: AccountWithDetailsForUrlRouting,
    options?: GetProfileUrlOptions,
): string {
    if (accountIsEnterpriseForUrlRouting(account)) {
        return getProfileUrl(account, options);
    } else if (accountIsOrganizationForUrlRouting(account)) {
        if (isEnterpriseOrganizationForUrlRouting(account) && "enterprise" in account) {
            return getProfileUrl(account, account.enterprise, options);
        } else {
            return getProfileUrl(account, options);
        }
    } else {
        return getProfileUrl(account, options);
    }
}

// ----- Deprecated old functions below:

/**
 * @deprecated Can't route correctly for organizations that are part of an enterprise - try something like getProfileUrlForAccountHandles instead
 */
export function getAccountProfileUrlFromHandle(handle: OrganizationHandle, options?: GetProfileUrlOptions): string;
/**
 * @deprecated Can't route correctly for organizations that are part of an enterprise - try something like getProfileUrlForAccountHandles instead
 */
export function getAccountProfileUrlFromHandle(handle: UserHandle, options?: GetProfileUrlOptions): string;
/**
 * @deprecated Can't route correctly for organizations that are part of an enterprise - try something like getProfileUrlForAccountHandles instead
 */
export function getAccountProfileUrlFromHandle(
    handle: AccountPublicHandleForUrlRouting,
    options: GetProfileUrlOptions = {},
): string {
    return joinPathParts("/", handle.toLowerCase(), options.subRoute) + getUrlParamsForProfileUrl(options);
}

/**
 * @deprecated Can't route correctly for organizations that are part of an enterprise
 */
export function getAccountProfileUrl(account: UserForUrlRouting | OrganizationForUrlRouting) {
    return getAccountProfileUrlFromHandle(account.handle);
}

/**
 * @deprecated Doesn't produce correct route for an organization belonging to an enterprise
 */
export function profileSettingsRoute(
    handle: OrganizationHandle,
    options: {paymentSuccess?: boolean} = {paymentSuccess: false},
): string {
    if (options?.paymentSuccess) {
        return `/${handle}/settings?paymentSuccess=true`;
    }
    return `/${handle}/settings`;
}

/**
 * @deprecated Doesn't produce correct route for an organization belonging to an enterprise
 */
export function userUpgradeRoute(handle: UserHandle): string {
    return `/${handle}/upgrade`;
}

/**
 * @deprecated not valid for reverse-routing an organization that belongs to an enterprise
 */
export function getOrganizationProfileUrlFromHandle(handle: string | OrganizationPublicHandle) {
    return joinPathParts("/", handle.toLowerCase());
}
